import * as XLSX from 'xlsx-js-style';
import {
  buildPhoneApiFields,
  parsePhoneForForm,
  resolveRegion,
  validatePhoneInput,
} from './phoneUtils';

const DEFAULT_PASSWORD = 'DefaultPass123!';

function normalizeHeader(header) {
  return String(header || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function mapRowToParsedUser(row) {
  if (!row || typeof row !== 'object') return null;

  const normalized = {};
  Object.entries(row).forEach(([key, value]) => {
    normalized[normalizeHeader(key)] = value != null ? String(value).trim() : '';
  });

  const name =
    normalized.name ||
    normalized['full name'] ||
    normalized.fullname ||
    normalized.full_name ||
    '';
  const email =
    normalized.email ||
    normalized['email address'] ||
    normalized['e-mail'] ||
    '';
  const phone =
    normalized.phone ||
    normalized['phone number'] ||
    normalized.mobile ||
    '';
  const phoneRegion =
    normalized['phone region'] ||
    normalized.region ||
    normalized.country ||
    normalized['country code'] ||
    '';
  const role = normalized.role || 'customer';
  const password =
    normalized.password ||
    normalized['default password'] ||
    normalized.defaultpassword ||
    DEFAULT_PASSWORD;

  if (!name || !email) return null;

  return {
    name,
    email: email.toLowerCase(),
    phone,
    phoneRegion: phoneRegion || null,
    role: normalizeRoleValue(role),
    password,
  };
}

export function normalizeRoleValue(role) {
  const r = String(role || 'customer')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-');
  if (r === 'enduser' || r === 'end-user') return 'customer';
  if (r === 'teamlead' || r === 'team_lead') return 'team-lead';
  if (r === 'superadmin' || r === 'super_admin') return 'super-admin';
  return r || 'customer';
}

export function resolveRoleFromList(roleValue, roles) {
  const normalized = normalizeRoleValue(roleValue);
  return roles.find((r) => {
    const value = (r.value || '').toLowerCase();
    const code = (r.roleCode || r.role_code || '')
      .toLowerCase()
      .replace(/_/g, '-');
    return value === normalized || code === normalized;
  });
}

export function buildRegistrationPayload(parsedRow, roles) {
  const selectedRole = resolveRoleFromList(parsedRow.role, roles);
  if (!selectedRole?.roleId) {
    throw new Error(`Invalid or unsupported role: ${parsedRow.role || 'unknown'}`);
  }

  const nameParts = parsedRow.name.trim().split(/\s+/).filter(Boolean);
  const email = parsedRow.email.trim().toLowerCase();

  const defaultRegion = resolveRegion(parsedRow.phoneRegion || 'KE');
  let phoneFields = { phone: '', phoneRegion: defaultRegion };
  if (parsedRow.phone?.trim()) {
    const parsed = parsePhoneForForm(parsedRow.phone, defaultRegion);
    phoneFields = buildPhoneApiFields(parsed.nationalNumber || parsedRow.phone, parsed.region);
  }

  return {
    email,
    username: email.split('@')[0],
    fullName: parsedRow.name.trim(),
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
    phone: phoneFields.phone,
    phoneRegion: phoneFields.phoneRegion,
    roleId: selectedRole.roleId,
    defaultPassword: parsedRow.password?.trim() || DEFAULT_PASSWORD,
  };
}

export function parseBulkUserCsv(text) {
  try {
    const workbook = XLSX.read(text, { type: 'string', raw: false });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
    return rows.map(mapRowToParsedUser).filter(Boolean);
  } catch {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => normalizeHeader(h));
    const users = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      const mapped = mapRowToParsedUser(row);
      if (mapped) users.push(mapped);
    }
    return users;
  }
}

export async function parseBulkUserExcel(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
  return rows.map(mapRowToParsedUser).filter(Boolean);
}

export async function parseBulkUserFile(file) {
  const fileName = file.name.toLowerCase();
  if (fileName.endsWith('.csv')) {
    const text = await file.text();
    return parseBulkUserCsv(text);
  }
  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return parseBulkUserExcel(file);
  }
  throw new Error('Unsupported file type. Use CSV or Excel (.xlsx, .xls).');
}

export function isDuplicateUserError(error) {
  const message = (
    error?.response?.data?.message ||
    error?.message ||
    ''
  ).toLowerCase();
  return (
    message.includes('already exists') ||
    message.includes('already registered') ||
    message.includes('email already') ||
    message.includes('username already') ||
    message.includes('phone')
  );
}

export function extractErrorText(error, fallback = 'Registration failed') {
  return error?.response?.data?.message || error?.message || fallback;
}

/**
 * Register users one-by-one via admin/super-admin API (supports roles + per-user duplicate handling).
 */
export async function registerBulkUsers(parsedUsers, roles, { checkEmail, checkPhone, registerUser }) {
  const created = [];
  const alreadyExists = [];
  const failed = [];
  const seenEmails = new Set();
  const seenPhones = new Set();

  for (const row of parsedUsers) {
    const email = row.email.trim().toLowerCase();

    if (seenEmails.has(email)) {
      alreadyExists.push({ ...row, reason: 'Duplicate email in uploaded file' });
      continue;
    }
    seenEmails.add(email);

    if (row.phone?.trim()) {
      const region = resolveRegion(row.phoneRegion || 'KE');
      const phoneValidation = validatePhoneInput(row.phone, region);
      if (!phoneValidation.valid) {
        failed.push({ ...row, reason: phoneValidation.message });
        continue;
      }
      const phoneFields = buildPhoneApiFields(row.phone, region);
      const e164Key = phoneFields.phoneE164 || `${region}:${row.phone}`;
      if (seenPhones.has(e164Key)) {
        alreadyExists.push({ ...row, reason: 'Duplicate phone in uploaded file' });
        continue;
      }
      seenPhones.add(e164Key);

      if (checkPhone) {
        try {
          const phoneAvailability = await checkPhone(
            phoneFields.phone,
            phoneFields.phoneRegion
          );
          if (phoneAvailability?.available === false) {
            alreadyExists.push({
              ...row,
              reason: phoneAvailability.message || 'Phone already exists',
            });
            continue;
          }
        } catch (checkErr) {
          failed.push({
            ...row,
            reason: extractErrorText(checkErr, 'Could not verify phone availability'),
          });
          continue;
        }
      }
    }

    try {
      const availability = await checkEmail(email);
      if (availability?.available === false) {
        alreadyExists.push({
          ...row,
          reason: availability.message || 'Email already exists',
        });
        continue;
      }
    } catch (checkErr) {
      failed.push({
        ...row,
        reason: extractErrorText(checkErr, 'Could not verify email availability'),
      });
      continue;
    }

    try {
      const payload = buildRegistrationPayload(row, roles);
      const response = await registerUser(payload);
      if (response?.data) {
        created.push({ ...row, email });
      } else {
        failed.push({ ...row, reason: 'No response from server' });
      }
    } catch (registerErr) {
      if (isDuplicateUserError(registerErr)) {
        alreadyExists.push({
          ...row,
          reason: extractErrorText(registerErr, 'User already exists'),
        });
      } else {
        failed.push({
          ...row,
          reason: extractErrorText(registerErr),
        });
      }
    }
  }

  return { created, alreadyExists, failed };
}

export function formatBulkRegistrationSummary(result) {
  const { created, alreadyExists, failed } = result;
  const parts = [];
  if (created.length) parts.push(`${created.length} created`);
  if (alreadyExists.length) parts.push(`${alreadyExists.length} already exist`);
  if (failed.length) parts.push(`${failed.length} failed`);
  return parts.join(', ') || 'No users processed';
}
